import { useEffect, useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import moment from 'moment';
import toast from 'react-hot-toast';

import httpClient from 'utils/httpClient';

// TimesheetStatus mappings
const TIMESHEET_STATUS_COLOR_MAP = {
  draft: 'default',
  under_review: 'warning',
  approved: 'success',
  rejected: 'error',
};

const TIMESHEET_STATUS_LABEL_MAP = {
  draft: 'Draft',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const formatDate = (date) => {
  if (!date) {
    return '-';
  }
  return moment(date).format('MMM DD, YYYY');
};

const formatDateTime = (dateTime) => {
  if (!dateTime) {
    return '-';
  }
  return moment(dateTime).format('MMM DD, YYYY, h:mm A');
};

export const TimesheetsTable = ({
  timesheets,
  formatDate,
  formatDateTime,
  onEdit,
  onDelete,
}) => {
  const [statusHovered, setStatusHovered] = useState(null);
  const [notesHovered, setNotesHovered] = useState(null);
  const [notesHover, setNotesHover] = useState({
    timesheetId: null,
    mouseX: 0,
    mouseY: 0,
    notes: '',
  });

  // Refs to store timeout IDs for debounce cancellation
  const notesEnterTimeoutRef = useRef(null);
  const notesMoveTimeoutRef = useRef(null);
  const notesLeaveTimeoutRef = useRef(null);

  // Debounced handlers for notes hover
  const handleNotesMouseEnter = (timesheet, event) => {
    // Clear any pending enter timeout
    if (notesEnterTimeoutRef.current) {
      clearTimeout(notesEnterTimeoutRef.current);
    }

    notesEnterTimeoutRef.current = setTimeout(() => {
      if (timesheet.description) {
        setNotesHovered(timesheet.id);
        setNotesHover({
          timesheetId: timesheet.id,
          mouseX: event.clientX,
          mouseY: event.clientY,
          notes: timesheet.description,
        });
      }
    }, 300);
  };

  const handleNotesMouseMove = (timesheet, event) => {
    // Clear any pending move timeout
    if (notesMoveTimeoutRef.current) {
      clearTimeout(notesMoveTimeoutRef.current);
    }

    notesMoveTimeoutRef.current = setTimeout(() => {
      if (notesHovered === timesheet.id && timesheet.description) {
        setNotesHover({
          timesheetId: timesheet.id,
          mouseX: event.clientX,
          mouseY: event.clientY,
          notes: timesheet.description,
        });
      }
    }, 100);
  };

  const handleNotesMouseLeave = () => {
    // Clear all pending timeouts
    if (notesEnterTimeoutRef.current) {
      clearTimeout(notesEnterTimeoutRef.current);
      notesEnterTimeoutRef.current = null;
    }
    if (notesMoveTimeoutRef.current) {
      clearTimeout(notesMoveTimeoutRef.current);
      notesMoveTimeoutRef.current = null;
    }

    // Immediate cleanup for leave
    notesLeaveTimeoutRef.current = setTimeout(() => {
      setNotesHovered(null);
      setNotesHover({ timesheetId: null, mouseX: 0, mouseY: 0, notes: '' });
    }, 200);
  };

  const handleStatusMouseEnter = (timesheetId) => {
    setStatusHovered(timesheetId);
  };

  const handleStatusMouseLeave = () => {
    setStatusHovered(null);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (notesEnterTimeoutRef.current)
        clearTimeout(notesEnterTimeoutRef.current);
      if (notesMoveTimeoutRef.current)
        clearTimeout(notesMoveTimeoutRef.current);
      if (notesLeaveTimeoutRef.current)
        clearTimeout(notesLeaveTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <TableContainer
        component={Paper}
        variant='outlined'
        sx={{ mt: 3, borderRadius: 1 }}
      >
        <Table size='small' aria-label='Timesheets table'>
          <TableHead sx={{ backgroundColor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Work Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timesheets.map((timesheet) => {
              // Build tooltip content for Status hover
              const statusTooltipContent =
                timesheet.rejection_reason &&
                timesheet.reviewed_at &&
                timesheet.status === 'rejected' ? (
                  <Box display='flex' flexDirection='column' gap={1}>
                    <Typography
                      variant='caption'
                      sx={{ color: 'common.white' }}
                    >
                      {formatDateTime(timesheet.reviewed_at)}
                    </Typography>
                    <Typography variant='caption'>
                      {timesheet.rejection_reason}
                    </Typography>
                  </Box>
                ) : null;

              return (
                <TableRow key={timesheet.id} hover>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontSize: '0.75rem' }}>
                      {formatDateTime(timesheet.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {formatDate(timesheet.work_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {timesheet.hours_display || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: timesheet.description ? 'help' : 'default',
                      }}
                      onMouseEnter={(e) => handleNotesMouseEnter(timesheet, e)}
                      onMouseMove={(e) => handleNotesMouseMove(timesheet, e)}
                      onMouseLeave={handleNotesMouseLeave}
                    >
                      {timesheet.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {statusTooltipContent ? (
                      <Tooltip
                        title={statusTooltipContent}
                        placement='left'
                        arrow
                        enterDelay={200}
                        onOpen={() => handleStatusMouseEnter(timesheet.id)}
                        onClose={handleStatusMouseLeave}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#374151',
                              color: 'common.white',
                              maxWidth: 400,
                              '& .MuiTooltip-arrow': {
                                color: '#374151',
                              },
                            },
                          },
                        }}
                      >
                        <Chip
                          label={
                            TIMESHEET_STATUS_LABEL_MAP[timesheet.status] ||
                            timesheet.status ||
                            'Draft'
                          }
                          color={
                            TIMESHEET_STATUS_COLOR_MAP[timesheet.status] ||
                            'default'
                          }
                          size='small'
                          variant='outlined'
                          sx={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label={
                          TIMESHEET_STATUS_LABEL_MAP[timesheet.status] ||
                          timesheet.status ||
                          'Draft'
                        }
                        color={
                          TIMESHEET_STATUS_COLOR_MAP[timesheet.status] ||
                          'default'
                        }
                        size='small'
                        variant='outlined'
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Floating notes tooltip that follows cursor */}
      <Popover
        open={notesHover.timesheetId !== null}
        anchorReference='anchorPosition'
        anchorPosition={{
          top: notesHover.mouseY + 10,
          left: notesHover.mouseX + 10,
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            bgcolor: '#1f2937',
            color: 'common.white',
            px: 1.5,
            py: 1,
            borderRadius: 1,
            maxWidth: 400,
            boxShadow:
              '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            pointerEvents: 'none',
          },
        }}
      >
        <Typography
          variant='caption'
          sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap' }}
        >
          {notesHover.notes}
        </Typography>
      </Popover>
    </>
  );
};

export const TimesheetEmptyState = ({
  projectId,
  onCreate,
  showAddButton = false,
}) => (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Typography variant='body1' color='text.secondary'>
      No timesheets found
    </Typography>
    {projectId && showAddButton && (
      <Button
        sx={{ mt: 2 }}
        startIcon={<AddIcon />}
        onClick={onCreate}
        aria-label='Create first timesheet'
      >
        Add Timesheet
      </Button>
    )}
  </Box>
);

export const TimesheetSummary = ({ stats }) => {
  if (!stats) return null;

  const totalApprovedHours = Number(stats.total_hours_approved || 0);
  const hoursUnderReview = Number(stats.hours_under_review || 0);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 3 },
        px: 4,
        py: 2.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>
          Total Timesheets
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {stats.total_timesheets || 0}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>
          Total Approved Hours
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {totalApprovedHours}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>
          Under Review Hours
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {hoursUnderReview}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>
          Approved Count
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {stats.approved_count || 0}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>
          Rejected Count
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          {stats.rejected_count || 0}
        </Typography>
      </Box>
    </Box>
  );
};

const ProjectTimesheetSection = ({
  projectId,
  project,
  showAddButton = false,
}) => {
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const isMountedRef = useRef(false);

  const fetchTimesheets = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    if (!projectId) {
      if (isMountedRef.current) {
        setTimesheets([]);
        setStats(null);
        setIsFetching(false);
      }
      return;
    }

    setIsFetching(true);

    try {
      const response = await httpClient.get(
        `/api/v1/project-management/timesheets/`,
        {
          params: {
            project: projectId,
          },
        }
      );

      const {
        status,
        message,
        data,
        results,
        items,
        stats: responseStats,
      } = response ?? {};

      if (status === false) {
        throw new Error(message || 'Failed to fetch timesheets');
      }

      const responseData = data || results || response || {};
      const timesheetResults =
        responseData.items ||
        items ||
        responseData.results ||
        responseData ||
        [];
      const timesheetStats = responseData.stats || responseStats || null;

      if (isMountedRef.current) {
        const nextTimesheets = Array.isArray(timesheetResults)
          ? timesheetResults
          : [];
        setTimesheets(nextTimesheets);
        setStats(timesheetStats);
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      toast.error(error?.message || 'Failed to fetch timesheets');
      if (isMountedRef.current) {
        setTimesheets([]);
        setStats(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchTimesheets();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchTimesheets]);

  const openCreateDialog = () => {
    setSelectedTimesheet(null);
    setActiveDialog('create');
  };

  const openEditDialog = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setActiveDialog('edit');
  };

  const closeFormDialog = () => {
    setActiveDialog(null);
    setSelectedTimesheet(null);
  };

  const handleFormSuccess = async () => {
    closeFormDialog();
    await fetchTimesheets();
  };

  const handleDeleteRequest = (timesheet, event) => {
    event?.currentTarget?.blur();
    setSelectedTimesheet(timesheet);
    setActiveDialog('delete');
  };

  const closeDeleteDialog = () => {
    setActiveDialog(null);
    setSelectedTimesheet(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTimesheet) return;

    try {
      const response = await httpClient.delete(
        `/api/v1/project-management/timesheets/${selectedTimesheet.id}/`
      );

      if (response?.status === false) {
        throw new Error(response?.message || 'Failed to delete timesheet');
      }

      toast.success(response?.message || 'Timesheet deleted successfully');
      closeDeleteDialog();
      await fetchTimesheets();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      toast.error(error?.message || 'Failed to delete timesheet');
    }
  };

  const hasTimesheets = timesheets.length > 0;

  return (
    <>
      <Paper sx={{ mb: 4, borderRadius: 2 }}>
        {isFetching && (
          <LinearProgress
            aria-label='Loading timesheets'
            sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
          />
        )}

        <Box sx={{ p: 3, pb: hasTimesheets ? 3 : 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Timesheets ({timesheets.length})
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Track work hours and time entries for this project.
              </Typography>
            </Box>

            {showAddButton && hasTimesheets && (
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                disabled={!projectId}
                aria-label='Add timesheet'
              >
                Add Timesheet
              </Button>
            )}
          </Box>

          {hasTimesheets ? (
            <TimesheetsTable
              timesheets={timesheets}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              onEdit={openEditDialog}
              onDelete={handleDeleteRequest}
            />
          ) : (
            <TimesheetEmptyState
              projectId={projectId}
              onCreate={openCreateDialog}
              showAddButton={showAddButton}
            />
          )}
        </Box>

        {hasTimesheets && <TimesheetSummary stats={stats} />}
      </Paper>
    </>
  );
};

export default ProjectTimesheetSection;
